import { useState } from "react";
import { LandingPageSectionApi, SectionFormValues, shopApi } from "api/shop";
import { IDropdownOption, PaziaButton } from "components/atoms";
import { SectionItem } from "./SectionItem";
import { AddSectionModal } from "./AddSectionModal";
import { EditSectionModal } from "./EditSectionModal";
import { useToaster } from "utils";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
} from "react-beautiful-dnd";

interface SectionsListProps {
  landingPageId: number;
  sections: LandingPageSectionApi[];
  onUpdate: () => void;
  typeOptions?: IDropdownOption[];
  onCreateSection?: (data: SectionFormValues) => Promise<void>;
  onUpdateSection?: (sectionId: number, data: SectionFormValues) => Promise<void>;
  onDeleteSection?: (sectionId: number) => Promise<void>;
  onReorderSections?: (sectionIds: number[]) => Promise<void>;
}

export const SectionsList = ({
  landingPageId,
  sections,
  onUpdate,
  typeOptions,
  onCreateSection,
  onUpdateSection,
  onDeleteSection,
  onReorderSections,
}: SectionsListProps) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<LandingPageSectionApi | null>(null);
  const { showSuccess, showError } = useToaster();

  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  const handleDelete = async (sectionId: number) => {
    if (!confirm("Czy na pewno chcesz usunąć tę sekcję?")) return;

    try {
      if (onDeleteSection) {
        await onDeleteSection(sectionId);
      } else {
        await shopApi.deleteSection(landingPageId, sectionId);
      }
      showSuccess("Usunięto sekcję");
      onUpdate();
    } catch (e) {
      showError("Nie udało się usunąć sekcji");
      console.error(e);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(sortedSections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const newOrder = items.map((item) => item.id);

    try {
      if (onReorderSections) {
        await onReorderSections(newOrder);
      } else {
        await shopApi.reorderSections(landingPageId, newOrder);
      }
      showSuccess("Zmieniono kolejność sekcji");
      onUpdate();
    } catch (e) {
      showError("Nie udało się zmienić kolejności sekcji");
      console.error(e);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Sekcje ({sections.length})</h3>
        <PaziaButton type="primary" title="Dodaj sekcję +" onClick={() => setIsAddModalOpen(true)} />
      </div>

      {sections.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-slate-300 p-8 text-center dark:border-slate-600">
          <p className="text-slate-500 dark:text-slate-400">
            Brak sekcji. Kliknij "Dodaj sekcję", aby dodać pierwszą sekcję do tej strony.
          </p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sections">
            {(provided: DroppableProvided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {sortedSections.map((section, index) => (
                  <Draggable key={section.id} draggableId={section.id.toString()} index={index}>
                    {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={snapshot.isDragging ? "opacity-50" : ""}
                      >
                        <SectionItem
                          section={section}
                          onEdit={() => setEditingSection(section)}
                          onDelete={() => handleDelete(section.id)}
                          dragHandleProps={provided.dragHandleProps}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <AddSectionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        landingPageId={landingPageId}
        typeOptions={typeOptions}
        onCreate={onCreateSection}
        onSuccess={() => {
          setIsAddModalOpen(false);
          onUpdate();
        }}
      />

      <EditSectionModal
        isOpen={!!editingSection}
        onClose={() => setEditingSection(null)}
        landingPageId={landingPageId}
        section={editingSection}
        onUpdate={onUpdateSection}
        onSuccess={() => {
          setEditingSection(null);
          onUpdate();
        }}
      />
    </div>
  );
};
